/**
 * Le maximum sur une répétition, **estimé** — la même formule des deux côtés.
 *
 * 🔴 **Sixième fichier de `shared/`, et le motif est celui de `search.ts`,
 * `macroTarget.ts` et `muscles.ts` : deux endroits calculent la même
 * grandeur.** Le tableau de bord en fait un classement par exercice
 * (`services/stats.ts`, étape 9) ; la fiche d'un exercice en trace la courbe
 * (`ExerciseChart`, 06/09). Deux écritures de la formule — ou pire, deux
 * plafonds de répétitions différents — auraient donné **deux maximums estimés
 * pour le même exercice selon l'écran d'où on l'ouvre**. C'est le symptôme que
 * personne ne signale : on se dit qu'on avait mal lu.
 *
 * 🔴 **Estimé, jamais un record.** Un 1RM calculé et un 1RM soulevé ne sont pas
 * la même chose, et le second n'est **jamais** mesuré par cette application.
 * Les deux écrans écrivent le mot « estimation » ; ce fichier ne rend qu'un
 * nombre, et c'est à l'appelant de ne pas le faire passer pour autre chose.
 */

/**
 * Plafond de répétitions au-delà duquel la formule ne veut plus rien dire.
 *
 * 🔴 **Ce n'est pas une prudence, c'est le domaine de validité.** Epley est une
 * approximation linéaire calée sur les séries courtes : à 25 répétitions elle
 * annoncerait 110 kg pour 60 kg soulevés, soit un « record » à quelqu'un qui
 * n'a peut-être jamais chargé 80. Franchir ce plafond en silence, ce serait
 * afficher un nombre inventé sous le mot « estimation ».
 */
export const EPLEY_MAX_REPS = 12;

/**
 * `charge × (1 + reps ÷ 30)`.
 *
 * `null` hors du domaine de validité — plus de {@link EPLEY_MAX_REPS}
 * répétitions, aucune charge, ou une charge nulle.
 *
 * ⚠️ **`null` et jamais zéro sur un exercice au poids du corps** : un zéro se
 * rangerait dans un classement et se lirait « record : 0 kg », ce qui est faux.
 * Il n'y a pas de record **nul**, il n'y a **pas de record à estimer**. C'est la
 * règle déjà tenue par la courbe de charge (« une courbe qui plonge à zéro
 * raconterait une régression qui n'a pas eu lieu »).
 */
export function epley(weightKg: number | null, reps: number | null): number | null {
  if (weightKg === null || reps === null) return null;
  if (weightKg <= 0 || reps < 1 || reps > EPLEY_MAX_REPS) return null;
  return Math.round(weightKg * (1 + reps / 30) * 10) / 10;
}
